import { CloseIcon, IconAudioMute, IconMic, IconPin, IconUnpin, IconVideo, IconVideoMute } from '@/assets/images';
import AppIcon from '@/components/AppIcon';
import { clearSearchCache } from '@/components/SearchBox';
import { ListTile, ListTileSkeleton, SearchBox, EmptyPlaceholder } from '@/components';
import { UserProfile, Username } from '@/features/users';
import { ExternalBadge } from '@/features/invite/ui';
import { useLanguage } from '@/localization/useLanguage';
import { MoreVertical, UserMinus } from 'lucide-react';
import PropTypes from 'prop-types';
import { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getLocalUserName, getCallStatusLabel } from '@/features/calls/utils/helper';
import { CALLKIT_CALLSTATUS } from '../../../constants';
import DropdownMenu from '../../CallLog/components/DropdownMenu';
import styles from './ParticipantsPanel.module.scss';
import { isEnabled } from '@/features/calls/subFeatureRegistry';

export default function ParticipantsPanel({
   participants = [],
   pinnedId = null,
   handlePin,
   onClose = () => {},
   isLoading = false,
}) {
   const { t } = useLanguage();
   const [searchTerm, setSearchTerm] = useState('');
   const [openDropdownId, setOpenDropdownId] = useState(null);

   const userInfoMap = useSelector(state => state.userInfoData?.users || {});
   const localUserId = getLocalUserName();

   useEffect(() => {
      return () => {
         clearSearchCache('participantsSearch');
      };
   }, []);

   const filteredParticipants = useMemo(() => {
      const query = searchTerm.toLowerCase().trim();
      return participants.filter(p => {
         const user = userInfoMap[p.id];
         const isLocal = p.id === localUserId;
         const displayName = isLocal ? t('common.YOU') : user?.nickName || p.name || user?.name || p.id;
         const actualName = user?.nickName || p.name || user?.name || '';

         return (
            displayName.toLowerCase().includes(query) ||
            actualName.toLowerCase().includes(query) ||
            p.id.toLowerCase().includes(query) ||
            (user?.name || '').toLowerCase().includes(query)
         );
      });
   }, [participants, searchTerm, userInfoMap, localUserId, t]);
   const isPinFeature = isEnabled('participantPin');
   const isRemoveFeature = isEnabled('participantRemove');

   const getParticipantOptions = (p, isPinned) => {
      const items = [];
      items.push({
         label: isPinned ? t('calls.UNPIN') : t('calls.PIN'),
         icon: isPinned ? <AppIcon size="lg" icon={<IconUnpin />} /> : <AppIcon size="lg" icon={<IconPin />} />,
         onClick: () => handlePin(p.id),
      });

      if (isRemoveFeature) {
         items.push({
            label: t('calls.REMOVE'),
            icon: <UserMinus size={18} />,
            onClick: () => {
               // Placeholder for remove from call functionality if exists
               console.log('Remove from call', p.id);
            },
         });
      }

      return items;
   };

   const getStatusLabel = status => {
      return getCallStatusLabel(status, t);
   };

   const renderItem = p => {
      const isPinned = p.id === pinnedId;
      const isLocal = p.id === localUserId;
      // Normalize case — see VideoTile's getStatusText for why (a differently-cased
      // "Connected" status can otherwise slip past this check and render as a label).
      const normalizedStatus = p.callStatus?.toUpperCase?.();
      const showStatus = !isLocal && normalizedStatus && normalizedStatus !== CALLKIT_CALLSTATUS.CONNECTED;

      return (
         <ListTile
            key={p.id}
            className={styles.participantItem}
            avatar={
               <UserProfile userId={p.id} name={p.name} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
            }
            title={
               <div
                  className="ct-flex ct-aiCenter ct-gap4"
                  title={p.isScreenSharing && !isLocal ? `${p.name} ( ${t('calls.SCREEN_PRESENTING')} )` : p.name}
               >
                  <div className="ct-flex ct-fcol ct-gap4">
                     <div className="ct-flex ct-aiCenter ct-gap6">
                        <Username userId={p.id} name={p.name} maxLength={9} />
                        {!isLocal && !p.isScreenSharing && (
                           <ExternalBadge
                              isExternalUser={userInfoMap[p.id]?.isExternal}
                              className="external-badge--compact"
                           />
                        )}
                     </div>
                     {isLocal && !p.isScreenSharing && <span className={styles.youText}>{t('common.YOU')}</span>}
                     {p.isScreenSharing && !isLocal && <p className={styles.youText}>{t('calls.SCREEN_PRESENTING')}</p>}
                  </div>
               </div>
            }
            subTitle={showStatus && <span className={styles.userStatusText}>{getStatusLabel(normalizedStatus)}</span>}
            trailing={
               <div className="ct-flex ct-aiCenter ct-gap12">
                  <div className={`ct-flex ct-aiCenter ct-gap16 ${styles.righticons}`}>
                     {isPinned && isPinFeature && (
                        <i className="ct-icon ct-lg ct-flexCenter">
                           <IconPin />
                        </i>
                     )}
                     {!p.isScreenSharing && (
                        <>
                           <i className="ct-icon ct-lg ct-flexCenter">
                              {p.isVideoMuted ? <IconVideoMute /> : <IconVideo />}
                           </i>
                           <i className="ct-icon ct-lg ct-flexCenter">
                              {p.isAudioMuted ? <IconAudioMute /> : <IconMic />}
                           </i>
                        </>
                     )}
                  </div>

                  {isPinFeature && (
                     <div className="ct-relative">
                        <button
                           className={`${styles.moreIcon} ct-flexCenter`}
                           onClick={e => {
                              e.stopPropagation();
                              setOpenDropdownId(openDropdownId === p.id ? null : p.id);
                           }}
                           aria-label={t('common.MORE_OPTIONS')}
                        >
                           <MoreVertical size={18} />
                        </button>
                        <DropdownMenu
                           isOpen={openDropdownId === p.id}
                           onClose={() => setOpenDropdownId(null)}
                           options={getParticipantOptions(p, isPinned)}
                           position="bottom-right"
                           className="participant-dropdown"
                        />
                     </div>
                  )}
               </div>
            }
         />
      );
   };

   const renderParticipantsList = () => {
      if (isLoading) {
         return Array.from({ length: 5 }).map((_, i) => <ListTileSkeleton key={`skele-pp-${i * 2 + 1}`} />);
      }
      if (filteredParticipants.length > 0) {
         return filteredParticipants.map(p => renderItem(p));
      }
      return <EmptyPlaceholder title={t('common.NO_PARTICIPANTS_FOUND')} />;
   };

   return (
      <aside className={`${styles.panel} ct-flex ct-fcol`}>
         <div className={`${styles.header} ct-flex ct-aiCenter ct-jcBetween`}>
            <strong>{t('calls.LOGS.PEOPLE_IN_CALL')}</strong>
            <button
               className="ct-flexCenter"
               onClick={() => {
                  clearSearchCache('participantsSearch');
                  onClose();
               }}
               aria-label={t('calls.LOGS.CLOSE')}
            >
               <CloseIcon />
            </button>
         </div>
         <div className={styles.searchContainer}>
            <div className="callScreenSearchbox">
               <SearchBox scopeKey="participantsSearch" placeholder={t('common.SEARCH')} onChange={setSearchTerm} />
            </div>
         </div>
         <div className={`${styles.list} ct-flex1 ct-scroll`}>{renderParticipantsList()}</div>
      </aside>
   );
}

ParticipantsPanel.propTypes = {
   participants: PropTypes.arrayOf(
      PropTypes.shape({
         id: PropTypes.string.isRequired,
         name: PropTypes.string,
         isVideoMuted: PropTypes.bool,
         isAudioMuted: PropTypes.bool,
         callStatus: PropTypes.string,
      }),
   ),
   pinnedId: PropTypes.string,
   handlePin: PropTypes.func,
   onClose: PropTypes.func,
   isLoading: PropTypes.bool,
};
